// src/config/flowRules.js
//
// Reglas de negocio del documento de pasos del cliente
// (gemini-code-1790188029694.md), como funciones puras:
//
//   · Plazos (sección 2): LOR 72 h, contacto del FA 48 h, NOI 45 días,
//     cheque emitido 7-10 días hábiles, endoso de mortgage 10-12 días hábiles.
//   · Bloqueos: lo que falta para poder completar un paso (p. ej. el Claim #
//     y el correo del DA al reportar; los 3 documentos del mortgage si el
//     cheque pasa de $40,000; la ruta "directo" solo < $10k o sin mortgage).
//
// Las usan el panel de pasos (qué mostrar y qué botón habilitar), el motor
// (flowService.completeStep valida antes de mover) y el listado de alertas.
// "Mover a…" del equipo CSR sigue siendo la salida manual, siempre con motivo.

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export const MORTGAGE_DOCS_THRESHOLD = 40000;
export const DIRECT_ROUTE_THRESHOLD = 10000;
export const MORTGAGE_DOCS = [
  { key: 'contractorEstimate', es: 'Estimado del contratista firmado por el cliente', en: 'Contractor estimate signed by the client' },
  { key: 'w9', es: 'W9 del contratista', en: 'Contractor W9' },
  { key: 'insurerEstimate', es: 'Estimado del seguro', en: 'Insurance estimate' }
];

// ── Fechas ────────────────────────────────────────────────────────────────

// Timestamp de Firestore, Date, milisegundos o 'YYYY-MM-DD' (medianoche local) → ms.
export const toMillis = value => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Suma días hábiles (lunes a viernes). Feriados: no se consideran (pendiente del cliente).
export const addBusinessDays = (startMs, days) => {
  const date = new Date(startMs);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  return date.getTime();
};

// ── Plazos ────────────────────────────────────────────────────────────────
// kind: 'deadline' (hay que actuar antes de dueAt), 'wait' (se espera a un
//       tercero; pasado dueAt hay que hacer seguimiento), 'unlock' (algo
//       queda habilitado recién en dueAt).
const TIMERS = {
  'claim:lor_sent': {
    kind: 'deadline',
    label: ['Enviar la LOR firmada al seguro (72 h desde la firma)', 'Send the signed LOR to the insurance company (72 h from signing)'],
    overdue: ['La LOR venció: hay que generarla y firmarla de nuevo.', 'The LOR expired: it must be generated and signed again.'],
    start: record => record.flow?.enteredAt,
    window: start => ({ warnAt: start + 48 * HOUR, dueAt: start + 72 * HOUR })
  },
  'claim:fa_contact': {
    kind: 'wait',
    label: ['Contacto del Field Adjuster (48 h)', 'Field Adjuster contact (48 h)'],
    overdue: ['Pasaron 48 h sin contacto del FA: haz seguimiento con el seguro.', '48 h without FA contact: follow up with the insurance company.'],
    start: record => record.flow?.enteredAt,
    window: start => ({ warnAt: start + 24 * HOUR, dueAt: start + 48 * HOUR })
  },
  'claim:attorney_noi': {
    kind: 'unlock',
    label: ['NOI: 45 días antes de poder pasar a litigio', 'NOI: 45 days before litigation can start'],
    ready: ['Pasaron los 45 días del NOI: ya se puede pasar a litigio.', 'The 45-day NOI period is over: litigation is enabled.'],
    // Sin fecha de NOI no corre el plazo.
    start: (record, stepData) => stepData?.noiDate,
    window: start => ({ warnAt: start + 38 * DAY, dueAt: start + 45 * DAY })
  },
  'check:issued': {
    kind: 'wait',
    label: ['Llegada del cheque (7-10 días hábiles)', 'Check arrival (7-10 business days)'],
    overdue: ['Pasaron 10 días hábiles y el cheque no llegó: llama al seguro.', '10 business days and the check has not arrived: call the insurance company.'],
    start: record => record.checkDate || record.flow?.enteredAt,
    window: start => ({ warnAt: addBusinessDays(start, 7), dueAt: addBusinessDays(start, 10) })
  },
  'check:mortgage_endorsement': {
    kind: 'wait',
    label: ['Retorno del cheque endosado (10-12 días hábiles)', 'Endorsed check return (10-12 business days)'],
    overdue: ['Pasaron 12 días hábiles sin el cheque endosado: llama al banco.', '12 business days without the endorsed check: call the bank.'],
    // Corre desde que se envía al banco.
    start: (record, stepData) => stepData?.sentDate,
    window: start => ({ warnAt: addBusinessDays(start, 10), dueAt: addBusinessDays(start, 12) })
  }
};

export const hasTimer = (type, stepId) => Boolean(TIMERS[`${type}:${stepId}`]);

// Plazo del paso actual, o null si el paso no tiene o todavía no arrancó
// (p. ej. reclamos migrados sin fecha de entrada: nunca alertan en falso).
// status: 'ok' | 'warning' | 'overdue' (deadline/wait) · 'waiting' | 'ready' (unlock)
export const getStepTimer = (type, record, stepData = {}, now = Date.now()) => {
  const stepId = record?.flow?.stepId;
  const def = stepId && TIMERS[`${type}:${stepId}`];
  if (!def) return null;
  const start = toMillis(def.start(record, stepData));
  if (!start) return null;
  const { warnAt, dueAt } = def.window(start);
  let status;
  if (def.kind === 'unlock') status = now >= dueAt ? 'ready' : 'waiting';
  else status = now >= dueAt ? 'overdue' : now >= warnAt ? 'warning' : 'ok';
  const message = status === 'overdue' ? def.overdue : status === 'ready' ? def.ready : null;
  return { key: `${type}:${stepId}`, kind: def.kind, label: def.label, message, startAt: start, warnAt, dueAt, status, remainingMs: dueAt - now };
};

// "en 2 día(s)", "hace 5 h"…
export const describeRemaining = (ms, es = true) => {
  const abs = Math.abs(ms);
  const value = abs >= DAY ? `${Math.round(abs / DAY)} ${es ? 'día(s)' : 'day(s)'}` : `${Math.max(1, Math.round(abs / HOUR))} h`;
  if (ms >= 0) return es ? `en ${value}` : `in ${value}`;
  return es ? `hace ${value}` : `${value} ago`;
};

// ── Bloqueos ──────────────────────────────────────────────────────────────
const need = (es, en) => ({ es, en });
const has = value => value !== null && value !== undefined && String(value).trim() !== '';
const amountOf = check => Number(check?.amount) || 0;

// ctx: { stepData, closing, checks } — stepData del paso actual; closing = el
// Closing del cheque; checks = cheques del reclamo (para cerrar el reclamo).
const STEP_BLOCKERS = {
  'claim:report_to_insurer': record => [
    !has(record.claimNumber) && need('Falta el número de reclamo (Claim #).', 'Missing the claim number.'),
    !has(record.deskAdjusterEmail) && need('Falta el correo del Desk Adjuster.', 'Missing the Desk Adjuster email.')
  ],
  'claim:lor_signature': record => [
    record.signedDocuments?.lor?.status !== 'signed' && need('Falta subir la LOR firmada por el cliente.', 'Upload the LOR signed by the client.')
  ],
  'claim:lor_sent': (record, { stepData }) => [
    !has(stepData?.lorSentToInsurerDate) && need('Falta la fecha en que se envió la LOR al seguro.', 'Missing the date the LOR was sent.')
  ],
  'check:issued': check => [
    !(amountOf(check) > 0) && need('Falta el monto del cheque.', 'Missing the check amount.')
  ],
  'check:mortgage_endorsement': (check, { stepData }) => [
    !has(stepData?.mailingAddress) && need('Falta la dirección de envío que dio el banco.', 'Missing the bank mailing address.'),
    !has(stepData?.trackingNumber) && need('Falta el tracking number del envío.', 'Missing the tracking number.'),
    ...(amountOf(check) > MORTGAGE_DOCS_THRESHOLD
      ? MORTGAGE_DOCS.filter(d => !stepData?.docs?.[d.key]?.storageKey)
        .map(d => need(`Cheque de más de $40,000: falta ${d.es}.`, `Check over $40,000: missing ${d.en}.`))
      : [])
  ],
  // Cobro interno de CSR (tramo B): valores reales y backup del cheque.
  'check:real_values': check => [
    !check.realValuesConfirmed && need('Faltan confirmar los valores reales del cheque.', 'Confirm the real check values.'),
    !check.checkAttachment?.storageKey && need('Falta el backup del cheque.', 'Missing the check backup.')
  ],
  'check:csr_payment': check => [
    !check.paid && need('Falta registrar el pago completo de la comisión.', 'Record the full commission payment.')
  ],
  'check:deposited': (check, { stepData }) => [
    !has(stepData?.depositDate) && need('Falta la fecha del depósito.', 'Missing the deposit date.')
  ],
  'check:breakdown': (check, { closing }) => [
    !closing && need('Falta armar el breakdown (Closing Statement de este cheque).', 'Build the breakdown (Closing Statement for this check).')
  ],
  'check:closing_statement': (check, { closing }) => [
    closing?.status !== 'signed' && need('Falta el Closing Statement firmado por el cliente.', 'Missing the Closing Statement signed by the client.')
  ],
  'check:disbursed': (check, { stepData }) => [
    !has(stepData?.disbursedDate) && need('Falta la fecha del desembolso al cliente.', 'Missing the disbursement date.')
  ]
};

// Cheques que todavía están en el tramo A (antes del desembolso al cliente).
const unfinishedChecks = checks => (checks || []).filter(c => c.flow?.phase === 'A');
const closeClaimBlocker = (record, { checks }) => {
  const open = unfinishedChecks(checks).length;
  return [open > 0 && need(`Hay ${open} cheque(s) sin terminar (falta llegar al desembolso).`, `${open} check(s) not finished yet (not disbursed).`)];
};

const OPTION_BLOCKERS = {
  'claim:collection:close': closeClaimBlocker,
  'claim:rcv:close': closeClaimBlocker,
  'check:endorsement_route:direct': check => [
    amountOf(check) >= DIRECT_ROUTE_THRESHOLD && check.hasMortgage
      && need('Directo solo aplica a cheques de menos de $10,000 o sin mortgage.', 'Direct only applies to checks under $10,000 or without a mortgage.')
  ],
  'claim:attorney_noi:litigation': (record, { stepData }) => {
    const timer = getStepTimer('claim', record, stepData);
    if (!timer) return [need('Falta la fecha del NOI (se cuentan 45 días desde ahí).', 'Missing the NOI date (45 days are counted from it).')];
    return [timer.status !== 'ready' && need(`El NOI necesita 45 días: se habilita ${describeRemaining(timer.remainingMs)}.`, `The NOI needs 45 days: enabled ${describeRemaining(timer.remainingMs, false)}.`)];
  }
};

// Lo que falta para completar el paso actual. Con `optionId` (decisiones)
// suma el bloqueo propio de esa opción.
export const getBlockers = (type, record, ctx = {}, optionId = null) => {
  const stepId = record?.flow?.stepId;
  if (!stepId) return [];
  const stepRule = STEP_BLOCKERS[`${type}:${stepId}`];
  return [
    ...(stepRule ? stepRule(record, ctx) : []),
    ...(optionId ? getOptionBlockers(type, record, optionId, ctx) : [])
  ].filter(Boolean);
};

// Bloqueo propio de una opción de decisión (para deshabilitar solo ese botón).
export function getOptionBlockers(type, record, optionId, ctx = {}) {
  const rule = OPTION_BLOCKERS[`${type}:${record?.flow?.stepId}:${optionId}`];
  return rule ? rule(record, ctx).filter(Boolean) : [];
}
